import AutocompleteBase, {
  AutocompleteBaseProps,
} from 'components/forms/fields/autocomplete-field/autocomplete-base/autocomplete-base.component';
import { useSearchParam } from 'components/search/search.hooks';
import { ChangeEvent } from 'react';
import { Option, handleMultiSelectChange } from 'util/multi-select';
import { useFocusFilter } from '../search-filters.hooks';
import { useRetainAutocompleteInputValue } from 'components/forms/fields/autocomplete-field/autocomplete-field.hooks';

type MultiSelectFilterProps = {
  searchParamName: string;
  'data-testid'?: string;
} & Partial<AutocompleteBaseProps>;

export const MultiSelectFilter = ({
  searchParamName,
  'data-testid': dataTestId = 'AutocompleteFilter',
  ...rest
}: MultiSelectFilterProps) => {
  const {
    value,
    setValue,
    options,
    searchParam: {
      filter: { label, componentProps },
      defaultValue,
      allOptionId,
    },
    setFocus,
  } = useSearchParam<string[]>(searchParamName);
  const typedComponentProps =
    (componentProps as Partial<AutocompleteBaseProps>) ?? {};
  const { ref, open, setOpen, readOnly } = useFocusFilter(setFocus);
  const { inputValue, handleInputChange } = useRetainAutocompleteInputValue();

  const onChange = (_e: ChangeEvent<HTMLSelectElement>, value: Option[]) =>
    setValue(
      handleMultiSelectChange(value, options, defaultValue, allOptionId).map(
        opt => opt.id,
      ),
    );

  return (
    <AutocompleteBase
      label={label}
      multiple={true}
      data-testid={dataTestId}
      options={options}
      value={options.filter(opt => value.includes(opt.id))}
      onChange={onChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      limitTags={1}
      ref={ref}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      inputProps={{ readOnly }}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      {...typedComponentProps}
      {...rest}
    />
  );
};

export default MultiSelectFilter;
